import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="suggested-categories"
// Shows suggested categories when an industry is selected (new enterprise)
// or immediately on connect (existing enterprise with known industry).
export default class extends Controller {
  static targets = ["panel", "industrySelect", "addedList", "counter", "jsonField", "submitWrapper"]
  static values = {
    suggestions: Object,
    existing: { type: Array, default: [] },
    enterpriseIdentifier: { type: String, default: "" }
  }

  connect() {
    this.addedCategories = []
    this.addedIdentifiers = new Set()

    // Build a set of existing category base identifiers (strip enterprise prefix)
    this.existingIdentifiers = new Set()
    const prefix = this.enterpriseIdentifierValue ? this.enterpriseIdentifierValue + "-" : ""
    this.existingValue.forEach(id => {
      this.existingIdentifiers.add(id)
      // Also store the base identifier (without enterprise prefix) for matching
      if (prefix && id.startsWith(prefix)) {
        this.existingIdentifiers.add(id.slice(prefix.length))
      }
    })

    // If industry is already selected (existing enterprise), render immediately
    if (this.hasIndustrySelectTarget) {
      const select = this.industrySelectTarget
      const selectedOption = select.options[select.selectedIndex]
      if (selectedOption && selectedOption.text && this.suggestionsValue[selectedOption.text]) {
        this.industryChanged()
      }
    }
  }

  industryChanged() {
    const select = this.industrySelectTarget
    const selectedOption = select.options[select.selectedIndex]
    const industryName = selectedOption ? selectedOption.text : ""
    const suggestions = this.suggestionsValue[industryName] || []

    if (suggestions.length === 0) {
      if (this.hasPanelTarget) this.panelTarget.classList.add("d-none")
      return
    }

    if (this.hasPanelTarget) this.panelTarget.classList.remove("d-none")
    this.renderSuggestions(industryName, suggestions)
  }

  renderSuggestions(industryName, suggestions) {
    const container = this.panelTarget.querySelector("[data-suggestions-list]")
    if (!container) return
    container.innerHTML = ""

    suggestions.forEach((cat, catIdx) => {
      const isAdded = this.addedIdentifiers.has(cat.identifier)
      const isExisting = this.isExistingCategory(cat.identifier)
      const card = document.createElement("div")
      card.className = "d-flex justify-content-between align-items-start p-2 mb-2 rounded"

      if (isExisting) {
        card.style.cssText = "background:#e8f5e9; border:1px solid #c8e6c9; opacity:0.5;"
      } else if (isAdded) {
        card.style.cssText = "background:#e8f5e9; border:1px solid #c8e6c9; opacity:0.7;"
      } else {
        card.style.cssText = "background:#f8f9fa; border:1px solid #eee; cursor:pointer;"
      }

      const subcatNames = (cat.subcategories || []).map(s => s.name).join(", ")

      let badge = ""
      if (isExisting) {
        badge = '<span class="badge bg-secondary" style="font-size:0.7rem;"><i class="fas fa-check"></i> Exists</span>'
      } else if (isAdded) {
        badge = '<span class="badge" style="background:var(--piktiz-green); color:white; font-size:0.7rem;"><i class="fas fa-check"></i> Added</span>'
      }

      card.innerHTML = `
        <div style="flex:1;">
          <div class="fw-medium" style="font-size:0.85rem;">${cat.name}</div>
          <div class="text-muted" style="font-size:0.75rem;">${subcatNames}</div>
        </div>
        <div>${badge}</div>
      `

      if (!isAdded && !isExisting) {
        card.addEventListener("click", () => this.addCategory(industryName, catIdx))
      }

      container.appendChild(card)
    })
  }

  isExistingCategory(identifier) {
    return this.existingIdentifiers.has(identifier)
  }

  addCategory(industryName, catIdx) {
    const suggestions = this.suggestionsValue[industryName] || []
    const cat = suggestions[catIdx]
    if (!cat || this.addedIdentifiers.has(cat.identifier) || this.isExistingCategory(cat.identifier)) return

    this.addedIdentifiers.add(cat.identifier)
    this.addedCategories.push(cat)
    this.syncJsonField()
    this.renderAddedList()
    this.updateCounter()
    this.updateSubmitVisibility()
    this.industryChanged()
  }

  removeCategory(event) {
    event.preventDefault()
    const identifier = event.currentTarget.dataset.identifier
    this.addedIdentifiers.delete(identifier)
    this.addedCategories = this.addedCategories.filter(c => c.identifier !== identifier)
    this.syncJsonField()
    this.renderAddedList()
    this.updateCounter()
    this.updateSubmitVisibility()
    this.industryChanged()
  }

  syncJsonField() {
    if (this.hasJsonFieldTarget) {
      this.jsonFieldTarget.value = JSON.stringify(this.addedCategories)
    }
  }

  renderAddedList() {
    const list = this.addedListTarget
    list.innerHTML = ""

    if (this.addedCategories.length === 0) return

    this.addedCategories.forEach(cat => {
      const block = document.createElement("div")
      block.className = "border rounded p-2 mb-2"
      block.style.cssText = "background:#f8f9fa;"

      const subcatBadges = (cat.subcategories || []).map(sub =>
        `<span class="badge bg-light text-dark me-1 mb-1" style="font-size:0.7rem; border:1px solid #ddd;">${sub.name}</span>`
      ).join("")

      block.innerHTML = `
        <div class="d-flex justify-content-between align-items-center mb-1">
          <span class="fw-medium" style="font-size:0.85rem;">${cat.name}</span>
          <button type="button" class="btn btn-sm text-danger" style="font-size:0.7rem;" data-action="click->suggested-categories#removeCategory" data-identifier="${cat.identifier}">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div>${subcatBadges}</div>
      `
      list.appendChild(block)
    })
  }

  updateCounter() {
    if (this.hasCounterTarget) {
      const count = this.addedCategories.length
      this.counterTarget.textContent = count
      this.counterTarget.classList.toggle("d-none", count === 0)
    }
  }

  updateSubmitVisibility() {
    if (this.hasSubmitWrapperTarget) {
      this.submitWrapperTarget.classList.toggle("d-none", this.addedCategories.length === 0)
    }
  }
}
